import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import usePermission from "../hooks/usePermission";
import useDefaultRedirect from "../../account/hooks/useDefaultRedirect";
import permissionContext from "../../core/context/permissionContext";
import { PermissionLevel } from "../../core/models/PermissionModels";

interface PermissionRouteGuardProps {
  /** The module to check permissions for (use Modules constants). */
  module: string;
  /** The minimum permission level required. Defaults to Read (1). */
  level?: PermissionLevel;
}

/**
 * Route-level permission guard. Renders Outlet if user has sufficient permission,
 * otherwise redirects to default route. Works alongside TypeGuard and AuthorizedRoute.
 *
 * During the transition period, if the JWT does not contain a permissions claim
 * (old token without permissions), access is allowed by default.
 *
 * Usage in routes:
 * ```tsx
 * <Route element={<PermissionRouteGuard module={Modules.Machinery} />}>
 *   <Route path="machinery" element={<Machinery />} />
 * </Route>
 * ```
 */
const PermissionRouteGuard = ({ module, level = PermissionLevel.Read }: PermissionRouteGuardProps) => {
  const { level: userLevel } = usePermission(module);
  const { defaultRoute } = useDefaultRedirect();

  // Transition mode: if the JWT has no permissions claim at all,
  // skip permission check and allow access (backward compatible).
  if (!permissionContext.hasPermissionsClaim()) {
    return <Outlet />;
  }

  if (userLevel >= level) {
    return <Outlet />;
  }

  return <Navigate to={defaultRoute()} replace />;
};

export default PermissionRouteGuard;
